import { useState } from 'react'
import { InterviewChecklistItem, ChecklistRating, ApplicantStatus, INTERVIEW_CHECKLIST_TEMPLATE } from '@shared/types/entities'

type ReviewResult = ApplicantStatus.APPROVED | ApplicantStatus.REJECTED | ApplicantStatus.REVISION_REQUIRED

interface InterviewChecklistProps {
  onSubmit: (checklist: InterviewChecklistItem[], result: ReviewResult, notes?: string) => void
  onCancel: () => void
  isSubmitting: boolean
}

const RATING_OPTIONS: { value: ChecklistRating; label: string; color: string }[] = [
  { value: 'BESTANDEN', label: 'Bestanden', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'TEILWEISE', label: 'Teilweise', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'NICHT_BESTANDEN', label: 'Nicht bestanden', color: 'bg-red-100 text-red-800 border-red-300' },
]

export function InterviewChecklist({ onSubmit, onCancel, isSubmitting }: InterviewChecklistProps) {
  const [checklist, setChecklist] = useState<InterviewChecklistItem[]>(
    INTERVIEW_CHECKLIST_TEMPLATE.map(item => ({ ...item, rating: null }))
  )
  const [notes, setNotes] = useState('')
  const [result, setResult] = useState<ReviewResult | null>(null)

  const updateRating = (id: string, rating: ChecklistRating) => {
    setChecklist(prev => prev.map(item =>
      item.id === id ? { ...item, rating } : item
    ))
  }

  const updateItemNotes = (id: string, itemNotes: string) => {
    setChecklist(prev => prev.map(item =>
      item.id === id ? { ...item, notes: itemNotes || undefined } : item
    ))
  }

  const allRated = checklist.every(item => item.rating !== null)

  const handleSubmit = () => {
    if (!result || !allRated) return
    onSubmit(checklist, result, notes || undefined)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Interview-Bewertung</h2>

          {/* Checklist Items */}
          <div className="space-y-4 mb-6">
            {checklist.map(item => (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900">{item.label}</span>
                    <span className="ml-2 text-xs text-gray-500">{item.category}</span>
                  </div>
                </div>
                <div className="flex gap-2 mb-2">
                  {RATING_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateRating(item.id, option.value)}
                      className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                        item.rating === option.value
                          ? `${option.color} ring-2 ring-offset-1 ring-current`
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Notizen (optional)"
                  value={item.notes || ''}
                  onChange={e => updateItemNotes(item.id, e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
            ))}
          </div>

          {/* Overall Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gesamtbewertung / Hinweise
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Optionale Hinweise für den Bewerber..."
            />
          </div>

          {/* Final Decision */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entscheidung
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setResult(ApplicantStatus.APPROVED)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  result === ApplicantStatus.APPROVED
                    ? 'bg-green-600 text-white ring-2 ring-green-300'
                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                }`}
              >
                Genehmigen
              </button>
              <button
                onClick={() => setResult(ApplicantStatus.REVISION_REQUIRED)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  result === ApplicantStatus.REVISION_REQUIRED
                    ? 'bg-yellow-600 text-white ring-2 ring-yellow-300'
                    : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                }`}
              >
                Überarbeitung
              </button>
              <button
                onClick={() => setResult(ApplicantStatus.REJECTED)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  result === ApplicantStatus.REJECTED
                    ? 'bg-red-600 text-white ring-2 ring-red-300'
                    : 'bg-red-50 text-red-700 hover:bg-red-100'
                }`}
              >
                Ablehnen
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={!allRated || !result || isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Wird gesendet...' : 'Bewertung absenden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
